import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedStoryCharacter } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { castDescriptionMessages as messages } from '../messages/castDescriptionMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StoryCharactersPromptProps {
  /** The recurring people of the chosen story who have no cast picture yet. */
  missing: readonly DirectedStoryCharacter[];
  disabled: boolean;
  /** False when the AI cannot make more pictures for this video. */
  canMake: boolean;
  onMake: (character: DirectedStoryCharacter) => void;
  /** The one forward action of the cast step (`castNextOf`): its label and its lock. */
  continueLabel: string;
  continueDisabled: boolean;
  /** Why the button is off ("Wait until every character is ready"), or null. */
  continueNote: string | null;
  onContinue: () => void;
}

/**
 * The cast step after a story with recurring people who have no picture (fix 2026-10-18: with no
 * picture, Veo made a different companion in every shot). One "Make a picture of …" button for each
 * person (the cast picture dialog, filled with the description of the story), and the forward
 * button of the cast step (the same label and lock as the foot, owner fix 2026-10-19).
 */
const StoryCharactersPromptComponent: React.FC<StoryCharactersPromptProps> = ({
  missing,
  disabled,
  canMake,
  onMake,
  continueLabel,
  continueDisabled,
  continueNote,
  onContinue,
}) => {
  const intl = useIntl();
  return (
    <div
      className={clsx(styles.alert, missing.length > 0 ? styles.alertWarn : styles.alertInfo)}
      role="status"
      data-testid="story-characters"
    >
      <div>
        {missing.length > 0 ? (
          <>
            <b>{intl.formatMessage(messages.charactersTitle, { count: missing.length })}</b>{' '}
            {intl.formatMessage(messages.charactersText)}
          </>
        ) : (
          intl.formatMessage(messages.charactersDone)
        )}
        <div className="d-flex flex-wrap gap-2 mt-2">
          {missing.map((character) => (
            <SMLButton
              key={character.name}
              className={styles.small}
              disabled={disabled || !canMake}
              onClick={() => onMake(character)}
            >
              {intl.formatMessage(messages.characterMake, { name: character.name })}
            </SMLButton>
          ))}
          <SMLButton
            className={styles.small}
            variant={missing.length > 0 ? 'default' : 'primary'}
            disabled={continueDisabled}
            onClick={onContinue}
          >
            {continueLabel}
          </SMLButton>
          {continueNote !== null && (
            <span className="align-self-center small" role="status">
              {continueNote}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const StoryCharactersPrompt = TypedMemo(StoryCharactersPromptComponent);
